import { Close } from "@mui/icons-material";
import { Box, Button, DialogActions, DialogContent, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { getAppointmentsCalendar, getExpiredAppointments } from "../../../../../actions/accidentDashboard";
import { attendedAppointment, unAttendedAppointment } from "../../../../../actions/appointments";
import snackbarUtils from "../../../../../utils/snackbarUtils";
import ConfirmDialog from "../../../../commons/dialog/confirmDialog";
import CustomDialog from "../../../../commons/dialog/customDialog";
import CardTitle from "../cardTitle";
import ExpiredAppointmentsTable from "./expiredAppointmentsTable";

const ExpiredAppointmentsDialog = ({
    open,
    handleClose,
    accident,
    appointments = [],
}) => {
    const dispatch = useDispatch();
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [selectedAppointments, setSelectedAppointments] = useState({});
    const [isProcessing, setIsProcessing] = useState(false);
    const [shouldCloseWhenEmpty, setShouldCloseWhenEmpty] = useState(false);
    const selectionLength = Object.keys(selectedAppointments).length;
    const pendingAppointmentsCount = useMemo(
        () => appointments.filter((appointment) => appointment.estado === "No informados").length,
        [appointments]
    );

    const handleOpenConfirmDialog = () => {
        setConfirmDialogOpen(true);
    };

    const handleCloseConfirmDialog = () => {
        setConfirmDialogOpen(false);
    };

    const handleSelectionChange = useCallback((selected) => {
        setSelectedAppointments(selected);
    }, []);

    useEffect(() => {
        if (open && shouldCloseWhenEmpty && pendingAppointmentsCount === 0) {
            setShouldCloseWhenEmpty(false);
            handleClose();
        }
    }, [open, shouldCloseWhenEmpty, pendingAppointmentsCount, handleClose]);

    const handleSetSelectionAttended = useCallback(async () => {
        setIsProcessing(true);
        try {
            const selectedIds = Object.keys(selectedAppointments);
            const wriId = accident.mlCloudCoreArtId;
            const promises = selectedIds.map(appointmentId =>
                dispatch(attendedAppointment(appointmentId, wriId))
            );
            await Promise.all(promises);

            // Actualizar turnos en el siniestro
            dispatch(getExpiredAppointments(accident.idSiniestro, accident.mlCloudCoreArtId));
            dispatch(getAppointmentsCalendar(accident.idSiniestro, accident.mlCloudCoreArtId));
            snackbarUtils.success("Turnos marcados como asistidos con éxito.");
            setSelectedAppointments({});
            setShouldCloseWhenEmpty(true);
        } catch (error) {
            snackbarUtils.error("Error al guardar los turnos como asistidos.")
        } finally {
            setIsProcessing(false);
        }
    }, [selectedAppointments, accident.idSiniestro, accident.mlCloudCoreArtId, dispatch]);

    const handleSetSelectionUnattended = useCallback(async () => {
        setIsProcessing(true);
        try {
            const selectedIds = Object.keys(selectedAppointments);
            const wriId = accident.mlCloudCoreArtId;
            const promises = selectedIds.map(appointmentId =>
                dispatch(unAttendedAppointment(appointmentId, wriId))
            );
            await Promise.all(promises);

            // Actualizar turnos en el siniestro
            dispatch(getExpiredAppointments(accident.idSiniestro, accident.mlCloudCoreArtId));
            dispatch(getAppointmentsCalendar(accident.idSiniestro, accident.mlCloudCoreArtId));
            snackbarUtils.success("Turnos marcados como inasistidos con éxito.");
            setSelectedAppointments({});
            setShouldCloseWhenEmpty(true);
        } catch (error) {
            snackbarUtils.error("Error al guardar los turnos como inasistidos.");
        } finally {
            setIsProcessing(false);
        }
    }, [selectedAppointments, accident.idSiniestro, accident.mlCloudCoreArtId, dispatch]);

    return (
        <CustomDialog open={open} maxWidth="lg" fullWidth>
            <CardTitle
                title="TURNOS"
                icons={[
                    {
                        Icon: Close,
                        tooltip: "Cerrar",
                        onClick: handleOpenConfirmDialog,
                        disabled: false
                    }
                ]}
            />
            <DialogContent>
                <Box sx={{ mb: 2 }}>
                    <Typography>
                        Se han detectado turnos vencidos o sin asistencia. Registre la asistencia correspondiente o inasistencia.
                    </Typography>
                </Box>
                <ExpiredAppointmentsTable
                    appointments={appointments}
                    onSelectionChange={handleSelectionChange}
                    rowSelection={selectedAppointments}
                />
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={handleSetSelectionAttended}
                    disabled={selectionLength === 0 || isProcessing}
                    variant="contained"
                    color="primary"
                >
                    {isProcessing ? 'PROCESANDO...' : 'GUARDAR ASISTENCIA'}
                </Button>
                <Button
                    onClick={handleSetSelectionUnattended}
                    disabled={selectionLength === 0 || isProcessing}
                    variant="contained"
                    color="warning"
                >
                    {isProcessing ? 'PROCESANDO...' : 'GUARDAR INASISTENCIA'}
                </Button>
            </DialogActions>
            <ConfirmDialog
                open={confirmDialogOpen}
                handleClose={handleCloseConfirmDialog}
                handleConfirm={handleClose}
                title="CERRAR VENTANA"
                text="¿Está seguro que desea cerrar esta ventana? En caso de aceptar, se perderán los cambios no guardados"
            />
        </CustomDialog>
    );
};

export default ExpiredAppointmentsDialog;